/**
 * QR Control Room, part 2 (v7 CORE-9):
 *   · BindFromFile: a file of label + record (XLSX or CSV), each row bound on its own (api.qr_bind_from_file);
 *   · TraceLookup: a scanned or typed code — what it is, what it is on, its prints and reprints, and a bottle's way
 *     (api.qr_trace);
 *   · DoorStickerCard: on Customer 360, the door's gate sticker — bind it, print or reprint it (a reprint with its
 *     reason) in the label editor. The CRM side may do this (Q5.3: qr.labels.doors).
 */
import { Fragment, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileUp, Printer, Search } from 'lucide-react';
import { rpc } from '../../lib/supabase';
import { useSession } from '../../lib/session';
import { Button, ErrorState, Panel, PanelHeader, cx, plainError } from '../../ui/primitives';
import { Field, Input } from '../../ui/forms';
import { Dialog } from '../../ui/overlays';
import { downloadXlsx, readSheet } from '../../ui/spreadsheet';
import { toast } from '../../ui/toast';
import * as f from '../../ui/format';
import { LabelEditor, type PrintedLabel, type Template } from './LabelEditor';

interface BindResult { bound: number; refused: number; detail: string; rows: { row: number; label: string; record: string; outcome: string; why?: string }[] }

export function BindFromFile() {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<BindResult | null>(null);
  const bind = useMutation({
    mutationFn: async (file: File) => {
      const rows = (await readSheet(file)).map((r) => ({ label: r.label ?? r.qr ?? r.code ?? '', record: r.record ?? r.on ?? r.record_code ?? '' }));
      if (!rows.length) throw new Error('The file has no rows under its header (label, record)');
      return rpc<BindResult>('qr_bind_from_file', { p_rows: rows, p_reason: reason.trim() });
    },
    onSuccess: (r) => { setResult(r); toast(r.detail); void qc.invalidateQueries({ queryKey: ['qr-overview'] }); },
  });
  return (
    <Panel>
      <PanelHeader title="Bind from a file" note="A label and the record it goes on, one per row" />
      <div className="grid gap-3 p-4">
        <div className="flex flex-wrap items-end gap-2">
          <Button onClick={() => downloadXlsx('qr-bind-template', [{ key: 'label', label: 'label' }, { key: 'record', label: 'record' }],
            [{ label: 'DSG-BAR-001234-5', record: 'BAR-0042' }, { label: 'DSG-JDH-00001-7', record: 'DSG-JDH-00001' }])}><Download size={14} aria-hidden /> Template</Button>
          <Field label="Why" className="min-w-[16rem] flex-1"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="The barrel sheet from the farm" /></Field>
          <label className={cx('inline-flex h-[32px] cursor-pointer items-center gap-1.5 rounded-[var(--radius-control)] border border-line-strong bg-surface px-3 text-body font-medium hover:bg-surface-sunken',
            (reason.trim().length < 5 || bind.isPending) && 'pointer-events-none opacity-50')}>
            <FileUp size={14} aria-hidden /> {bind.isPending ? 'Binding…' : 'Upload and bind'}
            <input type="file" accept=".xlsx,.csv" className="sr-only" onChange={(e) => { const file = e.target.files?.[0]; if (file) bind.mutate(file); e.target.value = ''; }} />
          </label>
        </div>
        <p className="text-small text-ink-muted">The record is its code: a door's customer code, a barrel's, a chiller's, a route's. A refused row changes nothing.</p>
        {bind.error ? <p role="alert" className="text-small text-refused">{plainError((bind.error as Error).message)}</p> : null}
        {result ? (
          <div className="max-h-80 overflow-auto rounded-md border border-line">
            <table className="w-full text-sm"><tbody>
              {result.rows.map((r) => (
                <tr key={r.row} className="border-t border-line first:border-0">
                  <td className="px-3 py-1 tabular">{r.row}</td><td className="px-3 py-1 font-mono">{r.label}</td><td className="px-3 py-1">{r.record}</td>
                  <td className={cx('px-3 py-1', r.outcome === 'BOUND' ? 'text-accepted' : 'text-refused')}>{r.outcome === 'BOUND' ? 'Bound' : r.why}</td>
                </tr>))}
            </tbody></table>
          </div>) : null}
      </div>
    </Panel>
  );
}

interface Trace { label: Record<string, unknown>; history: { at: string; what: string; why: string | null }[]; way: { occurred_at: string; action: string; customer_code: string | null; trip_code: string | null }[] | null }

export function TraceLookup({ initial }: { initial?: string }) {
  const [code, setCode] = useState(initial ?? ''); const [asked, setAsked] = useState((initial ?? '').toUpperCase());
  const q = useQuery({ queryKey: ['qr-trace', asked], enabled: asked.length > 3, queryFn: () => rpc<Trace>('qr_trace', { p_payload: asked }) });
  const facts = Object.entries(q.data?.label ?? {}).filter(([, v]) => v !== null && typeof v !== 'object');
  return (
    <Panel>
      <PanelHeader title="Look up a label" note="Scan into the box, or type the code" />
      <div className="grid max-w-4xl gap-4 p-4">
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); setAsked(code.trim().toUpperCase()); }}>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="DSG-BAR-001234-5" className="font-mono" autoFocus />
          <Button variant="primary" type="submit"><Search size={14} aria-hidden /> Look up</Button>
        </form>
        {q.error ? <ErrorState error={q.error} /> : q.data ? (
          <div className="grid gap-4 md:grid-cols-2">
            <dl className="grid grid-cols-[9rem_minmax(0,1fr)] content-start gap-x-3 gap-y-1 text-sm">
              {facts.map(([k, v]) => <Fragment key={k}><dt className="text-ink-muted">{k.replace(/_/g, ' ')}</dt><dd className="break-all">{String(v)}</dd></Fragment>)}
            </dl>
            <div className="grid content-start gap-3">
              <div><div className="text-small font-medium">Printed</div>
                {q.data.history.length === 0 ? <p className="text-small text-ink-muted">Never printed by the ERP.</p> : (
                  <ul className="text-small">{q.data.history.map((h, i) => <li key={i}>{f.dateTime(h.at)} · {h.what}{h.why ? ` · “${h.why}”` : ''}</li>)}</ul>)}
              </div>
              {q.data.way ? (
                <div><div className="text-small font-medium">The bottle's way</div>
                  <ul className="text-small">{q.data.way.slice(0, 20).map((w, i) => <li key={i}>{f.dateTime(w.occurred_at)} · {w.action.toLowerCase().replace(/_/g, ' ')}{w.customer_code ? ` · ${w.customer_code}` : ''}{w.trip_code ? ` · ${w.trip_code}` : ''}</li>)}</ul>
                </div>) : null}
            </div>
          </div>) : null}
      </div>
    </Panel>
  );
}

interface PrintJob { kind: 'PRINT' | 'REPRINT'; label_count: number; labels: PrintedLabel[] }

/** The sticker's own QR, small, with its full code beneath; a click opens it large to scan from the screen. */
function StickerQr({ code }: { code: string }) {
  const [svg, setSvg] = useState('');
  const [big, setBig] = useState(false);
  useEffect(() => {
    let live = true;
    void QRCode.toString(code, { type: 'svg', errorCorrectionLevel: 'M', margin: 2 }).then((x) => { if (live) setSvg(x); });
    return () => { live = false; };
  }, [code]);
  if (!svg) return null;
  const art = (size: string) => <div role="img" aria-label={`QR code ${code}`} className="rounded-md bg-white p-1 [&>svg]:h-full [&>svg]:w-full" style={{ width: size, height: size }} dangerouslySetInnerHTML={{ __html: svg }} />;
  return (
    <>
      <button type="button" onClick={() => setBig(true)} title="Click to enlarge" aria-label={`Enlarge the QR ${code}`}
        className="grid w-fit cursor-zoom-in justify-items-center gap-1 rounded-md border border-line p-2 hover:bg-surface-sunken">
        {art('7rem')}
        <span className="font-mono text-small">{code}</span>
      </button>
      <Dialog open={big} onClose={() => setBig(false)} title="Gate sticker QR" width="min(92vw, 26rem)">
        <div className="grid justify-items-center gap-3 p-4">
          {art('min(80vw, 20rem)')}
          <span className="break-all text-center font-mono text-body">{code}</span>
        </div>
      </Dialog>
    </>
  );
}

/** Customer 360 › the door's gate sticker. */
export function DoorStickerCard({ doorId, sticker }: { doorId: string; sticker: string | null }) {
  const { can } = useSession();
  const [reason, setReason] = useState('');
  const [job, setJob] = useState<PrintJob | null>(null);
  const mayPrint = can('masterdata.qr.manage') || can('qr.labels.doors');
  const templates = useQuery({ queryKey: ['qr-templates'], queryFn: () => rpc<Template[]>('qr_label_templates'), enabled: mayPrint && !!sticker });
  const bind = useMutation({ mutationFn: () => rpc<{ detail: string }>('bind_door_label', { p_location_id: doorId, p_payload: null, p_reason: 'Door sticker bound from the customer page', p_idempotency_key: null }),
    onSuccess: (r) => toast(r.detail, { tone: 'ok' }) });
  const print = useMutation({ mutationFn: () => rpc<PrintJob>('print_qr_labels', { p_class: null, p_serial_from: null, p_serial_to: null, p_payloads: [sticker], p_reason: reason.trim() || null }),
    onSuccess: (j) => setJob(j) });
  if (!sticker) return null;
  const err = (print.error ?? bind.error) as Error | null;
  const notBound = !!err && /not registered/.test(err.message);
  return (
    <div className="grid gap-2 border-t border-line px-4 py-3">
      <div className="text-small text-ink-muted">Gate sticker</div>
      <StickerQr code={sticker} />
      {mayPrint ? (
        <div className="flex flex-wrap items-end gap-2">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason, if it was printed before" className="min-w-[10rem] flex-1" />
          <Button size="dense" disabled={print.isPending} onClick={() => print.mutate()}><Printer size={13} aria-hidden /> Print</Button>
        </div>) : null}
      {notBound && can('customer.edit') ? <Button size="dense" onClick={() => bind.mutate()} disabled={bind.isPending}>Bind the sticker to this door first</Button> : null}
      {err && !notBound ? <p role="alert" className="text-small text-refused">{plainError(err.message)}</p> : null}
      {job && templates.data ? (
        <Dialog open onClose={() => setJob(null)} title={`${job.kind === 'REPRINT' ? 'Reprint' : 'Print'} the gate sticker`} width="min(96vw, 80rem)">
          <div className="p-4"><LabelEditor labels={job.labels} templates={templates.data ?? []} title="Recorded" onClose={() => setJob(null)} /></div>
        </Dialog>) : null}
    </div>
  );
}
